import {
  mysqlTable,
  int,
  bigint,
  varchar,
  text,
  mediumtext,
  boolean,
  decimal,
  timestamp,
  datetime,
  mysqlEnum,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { currencies } from "./finance";
import { taskboardColumns } from "./projects";

/**
 * companies — the tenant root. Every other tenant table carries `company_id` → companies.id
 * (reskin-docs/01 §3, 07 §1). This table itself has NO company_id.
 *
 * Ported line-by-line from database/migrations/2018_01_01_000000_create_worksuite_new_table.php
 * L95–166. Names/columns/defaults preserved verbatim ("schema is the contract", reskin-docs/01 §4).
 *
 * Deferred cross-batch FKs (kept as typed columns, constraints added when the target lands):
 *   currency_id → currencies · default_task_status → taskboard_columns · last_updated_by → users.
 */
export const companies = mysqlTable("companies", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyName: varchar("company_name", { length: 255 }).notNull(),
  appName: varchar("app_name", { length: 255 }),
  companyEmail: varchar("company_email", { length: 255 }).notNull(),
  companyPhone: varchar("company_phone", { length: 255 }).notNull(),
  logo: varchar("logo", { length: 255 }),
  lightLogo: varchar("light_logo", { length: 255 }),
  favicon: varchar("favicon", { length: 255 }),
  authTheme: mysqlEnum("auth_theme", ["dark", "light"]).default("light").notNull(),
  sidebarLogoStyle: mysqlEnum("sidebar_logo_style", ["square", "full"]).default("square").notNull(),
  loginBackground: varchar("login_background", { length: 255 }),
  address: text("address").notNull(),
  website: varchar("website", { length: 255 }),
  currencyId: int("currency_id", { unsigned: true }).references((): AnyMySqlColumn => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → currencies
  timezone: varchar("timezone", { length: 255 }).default("Asia/Kolkata").notNull(),
  dateFormat: varchar("date_format", { length: 20 }).default("d-m-Y").notNull(),
  datePickerFormat: varchar("date_picker_format", { length: 255 }).default("dd-mm-yyyy").notNull(),
  momentFormat: varchar("moment_format", { length: 255 }).default("DD-MM-YYYY").notNull(),
  timeFormat: varchar("time_format", { length: 20 }).default("h:i a").notNull(),
  locale: varchar("locale", { length: 255 }).default("en").notNull(),
  latitude: decimal("latitude", { precision: 10, scale: 8 }).default("26.9124336").notNull(),
  longitude: decimal("longitude", { precision: 11, scale: 8 }).default("75.7872709").notNull(),
  leavesStartFrom: mysqlEnum("leaves_start_from", ["joining_date", "year_start"])
    .default("joining_date")
    .notNull(),
  activeTheme: mysqlEnum("active_theme", ["default", "custom"]).default("default").notNull(),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }), // FK → users (deferred, circular)
  currencyConverterKey: varchar("currency_converter_key", { length: 255 }),
  googleMapKey: varchar("google_map_key", { length: 255 }),
  taskSelf: mysqlEnum("task_self", ["yes", "no"]).default("yes").notNull(),
  purchaseCode: varchar("purchase_code", { length: 100 }),
  licenseType: varchar("license_type", { length: 20 }),
  supportedUntil: timestamp("supported_until"),
  googleRecaptchaStatus: mysqlEnum("google_recaptcha_status", ["active", "deactive"])
    .default("deactive")
    .notNull(),
  googleRecaptchaV2Status: mysqlEnum("google_recaptcha_v2_status", ["active", "deactive"])
    .default("deactive")
    .notNull(),
  googleRecaptchaV2SiteKey: varchar("google_recaptcha_v2_site_key", { length: 255 }),
  googleRecaptchaV2SecretKey: varchar("google_recaptcha_v2_secret_key", { length: 255 }),
  googleRecaptchaV3Status: mysqlEnum("google_recaptcha_v3_status", ["active", "deactive"])
    .default("deactive")
    .notNull(),
  googleRecaptchaV3SiteKey: varchar("google_recaptcha_v3_site_key", { length: 255 }),
  googleRecaptchaV3SecretKey: varchar("google_recaptcha_v3_secret_key", { length: 255 }),
  appDebug: boolean("app_debug").default(false).notNull(),
  roundedTheme: boolean("rounded_theme").default(true).notNull(),
  hideCronMessage: boolean("hide_cron_message").default(false).notNull(),
  systemUpdate: boolean("system_update").default(true).notNull(),
  logoBackgroundColor: varchar("logo_background_color", { length: 255 }).default("#ffffff").notNull(),
  beforeDays: int("before_days").notNull(),
  afterDays: int("after_days").notNull(),
  onDeadline: mysqlEnum("on_deadline", ["yes", "no"]).default("yes").notNull(),
  defaultTaskStatus: int("default_task_status", { unsigned: true }).references(
    (): AnyMySqlColumn => taskboardColumns.id,
    { onUpdate: "cascade" },
  ), // FK → taskboard_columns (made nullable, default null by 2022_09_01_000000)
  showReviewModal: boolean("show_review_modal").default(true).notNull(),
  dashboardClock: boolean("dashboard_clock").default(true).notNull(),
  ticketFormGoogleCaptcha: boolean("ticket_form_google_captcha").default(false).notNull(),
  leadFormGoogleCaptcha: boolean("lead_form_google_captcha").default(false).notNull(),
  taskboardLength: int("taskboard_length").default(10).notNull(),
  lastCronRun: timestamp("last_cron_run"),
  sessionDriver: mysqlEnum("session_driver", ["file", "database"]).default("file").notNull(),
  allowClientSignup: boolean("allow_client_signup").notNull(),
  adminClientSignupApproval: boolean("admin_client_signup_approval").notNull(),
  allowedFileTypes: text("allowed_file_types"),
  googleCalendarStatus: mysqlEnum("google_calendar_status", ["active", "inactive"])
    .default("inactive")
    .notNull(),
  googleClientId: text("google_client_id"),
  googleClientSecret: text("google_client_secret"),
  googleCalendarVerificationStatus: mysqlEnum("google_calendar_verification_status", [
    "verified",
    "non_verified",
  ])
    .default("non_verified")
    .notNull(),
  googleId: varchar("google_id", { length: 255 }),
  name: varchar("name", { length: 255 }),
  token: text("token"),
  allowedFileSize: int("allowed_file_size").default(10).notNull(),
  currencyKeyVersion: mysqlEnum("currency_key_version", ["free", "api"]).default("free").notNull(),
  // Added by later up() ALTER migrations (reconciled to final shape):
  datatableRowLimit: int("datatable_row_limit").default(10).notNull(), // 2023_02_07
  headerColor: varchar("header_color", { length: 255 }).default("#1D82F5").notNull(), // 2022_12_12
  employeeCanExportData: boolean("employee_can_export_data").default(true).notNull(), // 2023_09_05
  yearStartsFrom: varchar("year_starts_from", { length: 255 }).default("1").notNull(), // 2022_10_09
  pmType: varchar("pm_type", { length: 255 }), // 2022_12_28
  pmLastFour: varchar("pm_last_four", { length: 255 }), // 2022_12_28
  authThemeText: mysqlEnum("auth_theme_text", ["dark", "light"]).default("dark").notNull(), // 2023_04_19
  status: mysqlEnum("status", ["active", "inactive"]).default("active").notNull(), // 2022_09_01_000000
  lastLogin: datetime("last_login"), // 2022_09_01_000000
  rtl: boolean("rtl").default(false).notNull(), // 2022_09_01_000000
  hash: varchar("hash", { length: 255 }), // 2022_09_01_000000
  showNewWebhookAlert: boolean("show_new_webhook_alert").default(false).notNull(), // 2022_11_23
  locationDetails: text("location_details"), // 2024_04_15
  headers: text("headers"), // 2024_02_07
  registerIp: varchar("register_ip", { length: 255 }), // 2024_02_07
  mattermostWebhookUrl: varchar("mattermost_webhook_url", { length: 500 }), // BiometricSync 2026_05_09
  missingPunchAlertEnabled: boolean("missing_punch_alert_enabled").default(false).notNull(), // BiometricSync 2026_05_20
  missingPunchMmChannelId: varchar("missing_punch_mm_channel_id", { length: 64 }), // BiometricSync 2026_05_20
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * company_addresses — billing/branch addresses per company (one flagged `is_default`).
 * Ported line-by-line from the base migration L266–275.
 */
export const companyAddresses = mysqlTable("company_addresses", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  address: mediumtext("address").notNull(),
  isDefault: boolean("is_default").notNull(),
  taxNumber: varchar("tax_number", { length: 255 }),
  taxName: varchar("tax_name", { length: 255 }),
  location: varchar("location", { length: 255 }),
  latitude: decimal("latitude", { precision: 10, scale: 8 }), // 2022_12_05
  longitude: decimal("longitude", { precision: 11, scale: 8 }), // 2022_12_05
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
